import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FileText, Search, RefreshCw, Copy, Send, Check, X, AlertTriangle,
  ShieldCheck, History, Download, Plus, Trash2, FileSignature, FileDown, Building, Receipt,
  Upload, FileCheck2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import {
  contractsApi, crmApi, downloadFile, MAX_SCOPE_ITEMS, type CrmClient, type CrmInvoice, type CrmQuote, type CrmProduct,
  type Opportunity, type QuoteLineInput, type QuoteScopeItem,
} from '../services/api';
import { useCrmSellerStore } from '../store/useCrmSellerStore';
import SellingAs from '../components/crm/SellingAs';
import QuoteDocumentUpload from '../components/crm/QuoteDocumentUpload';

/** Minor units to a display string. The only place money becomes text. */
function money(minor: number, currency = 'USD'): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, maximumFractionDigits: 2,
    }).format((minor || 0) / 100);
  } catch {
    return `${((minor || 0) / 100).toFixed(2)} ${currency}`;
  }
}

const STATUS_STYLE: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  issued: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  expired: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
};

export default function QuotesPage() {
  const [quotes, setQuotes] = useState<CrmQuote[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [products, setProducts] = useState<CrmProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<CrmQuote | 'new' | null>(null);
  const sellerOrgId = useCrmSellerStore((s) => s.sellerOrgId);
  const nameOf = useCrmSellerStore((s) => s.nameOf);
  const loadSellers = useCrmSellerStore((s) => s.loadSellers);
  const [partnerNames, setPartnerNames] = useState<Record<string, string>>({});
  // Arriving from an opportunity's "New quote": open the editor on that deal.
  const [params, setParams] = useSearchParams();
  const [presetOpportunity, setPresetOpportunity] = useState('');
  const navigate = useNavigate();
  // The live (not void) invoice of each quote, so an accepted quote shows
  // either its invoice or the way to raise one.
  const [invoiceByQuote, setInvoiceByQuote] = useState<Record<string, CrmInvoice>>({});
  // The quote whose signed copy or SOW is being uploaded.
  const [uploading, setUploading] = useState<{ quote: CrmQuote; kind: 'signed_quote' | 'sow' } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Every catalogue's SKUs: no product is tied to one company yet, so any
      // SKU can be quoted under any seller.
      const [q, o, c, inv] = await Promise.all([
        crmApi.listQuotes(undefined, sellerOrgId),
        crmApi.listOpportunities({ sellerOrgId }),
        crmApi.listCatalog(),
        crmApi.listInvoices().catch(() => ({ invoices: [] as CrmInvoice[] })),
      ]);
      setQuotes(q.quotes || []);
      setInvoiceByQuote(Object.fromEntries((inv.invoices || [])
        .filter((i) => i.status !== 'void').map((i) => [i.quoteId, i])));
      setOpportunities(o.opportunities || []);
      setProducts(c.products || []);
    } catch (err: any) {
      toast.error(err.message || 'Could not load quotes');
    } finally {
      setLoading(false);
    }
  }, [sellerOrgId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    loadSellers();
    crmApi.listClients(undefined, undefined, { includeArchived: true })
      .then((r) => setPartnerNames(Object.fromEntries(
        (r.clients || []).filter((c) => c.orgId).map((c) => [c.orgId!.toUpperCase(), c.name]))))
      .catch(() => setPartnerNames({}));
  }, [loadSellers]);
  useEffect(() => {
    const opp = params.get('opportunityId');
    if (!opp || loading) return;
    // The deal may be sold by a company other than the one this session is
    // filtered to. Fetched and added rather than left out, or the editor would
    // fall back to the first deal listed and price the wrong one.
    if (!opportunities.some((o) => o.id === opp)) {
      crmApi.getOpportunity(opp)
        .then((o) => setOpportunities((os) => [o, ...os]))
        .catch(() => toast.error('That opportunity could not be found'));
      return;
    }
    setPresetOpportunity(opp);
    setEditing('new');
    // Consumed once, so closing the editor and refreshing does not reopen it.
    params.delete('opportunityId');
    setParams(params, { replace: true });
  }, [params, setParams, loading, opportunities]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return quotes;
    return quotes.filter((q) =>
      [q.quoteNumber, q.title].some((v) => (v || '').toLowerCase().includes(s)));
  }, [quotes, search]);

  const act = async (fn: () => Promise<any>, ok: string) => {
    try {
      await fn();
      toast.success(ok);
      load();
    } catch (err: any) {
      toast.error(err.message || 'That did not work');
    }
  };

  /** A draft invoice from an accepted quote; numbered when issued on Invoices. */
  const createInvoice = async (q: CrmQuote) => {
    try {
      const inv = await crmApi.createInvoice(q._id);
      toast.success(`Draft invoice created for ${q.quoteNumber} — issue it to number it`);
      navigate(`/crm/invoices?focus=${encodeURIComponent(inv._id)}`);
    } catch (err: any) {
      toast.error(err.message || 'Could not create the invoice', { duration: 9000 });
    }
  };

  /** Raise the order form this quote prices.
   *
   *  Confirmed rather than immediate: it creates a contract record against the
   *  published terms, and the version it pins is what the client is later held
   *  to. The server refuses a draft quote, a second order form, or unpublished
   *  terms — its message says which, so it is shown verbatim.
   */
  const generateOrderForm = async (q: CrmQuote) => {
    if (!window.confirm(
      `Raise the order form for ${q.quoteNumber}?\n\n` +
      `It carries this quote's lines and total (${money(q.totalMinor)}) and is ` +
      `executed under the partner's master agreement. Created as a draft — ` +
      `nothing is sent.`)) return;
    try {
      const item = await contractsApi.generateOrderForm(q._id);
      toast.success(`Raised ${item.key || 'the order form'} — see Contracts`);
    } catch (err: any) {
      toast.error(err.message || 'Could not raise the order form');
    }
  };

  /** The branded PDF. Fetched with the token rather than linked — a plain
   *  anchor navigates without the Authorization header and renders the 401. */
  const exportQuotePdf = async (q: CrmQuote) => {
    try {
      await downloadFile(`/crm/quotes/${q._id}/export.pdf`,
        `${q.quoteNumber}-v${q.version}.pdf`);
    } catch (err: any) {
      toast.error(err.message || 'Could not build the PDF');
    }
  };

  /** The editable Word copy. Same parties and content as the PDF; it says on
   *  its face that the PDF of this number and version is the issued quote. */
  const exportQuoteDocx = async (q: CrmQuote) => {
    try {
      await downloadFile(`/crm/quotes/${q._id}/export.docx`,
        `${q.quoteNumber}-v${q.version}.docx`);
    } catch (err: any) {
      toast.error(err.message || 'Could not build the Word document');
    }
  };

  // Which quote's Download menu is open.
  const [downloadFor, setDownloadFor] = useState<string | null>(null);

  const exportQuote = async (q: CrmQuote) => {
    try {
      const doc = await crmApi.exportQuote(q._id);
      const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${q.quoteNumber}-v${q.version}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err.message || 'Export failed');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <FileText size={20} className="text-accent" />
            Quotes
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Raised against an opportunity. Editing an issued quote creates a new
            version — what the customer was sent stays readable.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <SellingAs />
          <button
            onClick={() => { setPresetOpportunity(''); setEditing('new'); }}
            disabled={opportunities.length === 0}
            title={opportunities.length === 0 ? 'Create an opportunity first' : ''}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50"
          >
            <Plus size={14} /> New quote
          </button>
          <button
            onClick={load}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-accent hover:text-accent"
          >
            <RefreshCw size={14} />
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Quote number or title…"
            className="pl-8 pr-3 py-1.5 w-64 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent"
          />
        </div>
        <span className="text-xs text-gray-400 ml-auto">
          {filtered.length} of {quotes.length}
        </span>
      </div>

      {quotes.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileText size={44} className="text-gray-300 dark:text-gray-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-300 font-medium">No quotes yet</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
            {opportunities.length === 0
              ? 'A quote is raised against an opportunity — create one first.'
              : 'Create a quote against an opportunity to get started.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map((q) => (
            <div key={q._id} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
              <div className="flex items-start gap-3">
                <button onClick={() => setEditing(q)} className="min-w-0 flex-1 text-left">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
                      {q.quoteNumber}
                    </span>
                    {q.version > 1 && (
                      <span className="flex items-center gap-1 text-[10px] text-gray-400">
                        <History size={9} /> v{q.version}
                      </span>
                    )}
                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${STATUS_STYLE[q.status] || ''}`}>
                      {q.status}
                    </span>
                    {q.requiresApproval && !q.approvedBy && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                        <ShieldCheck size={9} /> needs approval
                      </span>
                    )}
                    {q.discountCapped && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300">
                        <AlertTriangle size={9} /> capped at {q.discountCapPct}%
                      </span>
                    )}
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-0.5">
                    {q.title || '(untitled)'}
                  </div>
                  {(q.sellerOrgId || q.partnerOrgId) && (
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {q.sellerOrgId && (
                        <span className="flex items-center gap-1" title="Issued by">
                          <Building size={11} /> {nameOf(q.sellerOrgId)}
                        </span>
                      )}
                      {q.partnerOrgId && (
                        <span title="Sold through">
                          via {partnerNames[q.partnerOrgId.toUpperCase()] || q.partnerOrgId}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {q.lines?.length || 0} line{(q.lines?.length || 0) === 1 ? '' : 's'}
                    {q.totalDiscountMinor > 0 &&
                      ` · ${q.totalDiscountPct.toFixed(1)}% discount`}
                    {q.taxMinor > 0 && ` · tax ${money(q.taxMinor)}`}
                    {(q.scopeItems?.length || 0) > 0 &&
                      ` · ${q.scopeItems!.length} milestone${q.scopeItems!.length === 1 ? '' : 's'}`}
                    {q.sowRef && ` · SOW ${q.sowRef}`}
                  </div>
                </button>
                {/* What came back for this quote, and the way to add it. Not
                    inside the row's button: these are their own actions. */}
                {(q.status === 'issued' || q.status === 'accepted') && (
                  <div className="flex flex-wrap items-center gap-1.5 self-end">
                    {q.signedDocumentId ? (
                      <button onClick={() => void downloadFile(`/attachments/${q.signedDocumentId}/download`, q.signedFilename || 'signed-quote')}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:underline">
                        <FileCheck2 size={11} /> Signed copy
                      </button>
                    ) : (
                      <button onClick={() => setUploading({ quote: q, kind: 'signed_quote' })}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent hover:text-accent">
                        <Upload size={11} /> Upload signed quote
                      </button>
                    )}
                    {q.sowDocumentId ? (
                      <button onClick={() => void downloadFile(`/attachments/${q.sowDocumentId}/download`, q.sowFilename || 'statement-of-work')}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:underline">
                        <FileCheck2 size={11} /> SOW
                      </button>
                    ) : (
                      <button onClick={() => setUploading({ quote: q, kind: 'sow' })}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent hover:text-accent">
                        <Upload size={11} /> Upload SOW
                      </button>
                    )}
                  </div>
                )}

                <div className="shrink-0 text-right">
                  <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                    {money(q.totalMinor)}
                  </div>
                  {q.localCurrency !== 'USD' && (
                    <div className="text-[10px] text-gray-400">
                      {money(q.totalLocalMinor, q.localCurrency)}
                    </div>
                  )}
                  {q.status === 'accepted' && (
                    invoiceByQuote[q._id] ? (
                      <button
                        onClick={() => navigate(`/crm/invoices?focus=${encodeURIComponent(invoiceByQuote[q._id]._id)}`)}
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 hover:underline"
                      >
                        <Receipt size={11} /> {invoiceByQuote[q._id].number || 'Draft invoice'}
                      </button>
                    ) : (
                      <button
                        onClick={() => void createInvoice(q)}
                        className="mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] bg-accent text-white hover:opacity-90"
                      >
                        <Receipt size={11} /> Create invoice
                      </button>
                    )
                  )}
                  <div className="flex items-center gap-1 justify-end mt-1.5">
                    {q.status === 'draft' && (
                      <button
                        onClick={() => act(() => crmApi.transitionQuote(q._id, 'issued'), 'Quote issued')}
                        title="Issue"
                        className="p-1 rounded text-gray-400 hover:text-accent"
                      >
                        <Send size={13} />
                      </button>
                    )}
                    {q.requiresApproval && !q.approvedBy && (
                      <button
                        onClick={() => act(() => crmApi.approveQuote(q._id), 'Approved')}
                        title="Approve discount"
                        className="p-1 rounded text-gray-400 hover:text-emerald-500"
                      >
                        <ShieldCheck size={13} />
                      </button>
                    )}
                    {(q.status === 'issued' || q.status === 'accepted') && (
                      <button
                        onClick={() => void generateOrderForm(q)}
                        title={`Raise the order form for ${q.quoteNumber} under the partner's master agreement`}
                        className="p-1 rounded text-gray-400 hover:text-accent"
                      >
                        <FileSignature size={13} />
                      </button>
                    )}
                    {q.status === 'issued' && (
                      <>
                        <button
                          onClick={() => act(() => crmApi.transitionQuote(q._id, 'accepted'), 'Accepted')}
                          title="Mark accepted"
                          className="p-1 rounded text-gray-400 hover:text-emerald-500"
                        >
                          <Check size={13} />
                        </button>
                        <button
                          onClick={() => act(() => crmApi.transitionQuote(q._id, 'rejected'), 'Marked rejected')}
                          title="Mark rejected"
                          className="p-1 rounded text-gray-400 hover:text-rose-500"
                        >
                          <X size={13} />
                        </button>
                      </>
                    )}
                    <button
                      onClick={() => act(() => crmApi.duplicateQuote(q._id), 'Duplicated')}
                      title="Duplicate"
                      className="p-1 rounded text-gray-400 hover:text-accent"
                    >
                      <Copy size={13} />
                    </button>
                    <div className="relative">
                      <button
                        onClick={() => setDownloadFor(downloadFor === q._id ? null : q._id)}
                        title="Download as PDF or Word"
                        className="p-1 rounded text-gray-400 hover:text-accent"
                      >
                        <Download size={13} />
                      </button>
                      {downloadFor === q._id && (
                        <>
                          {/* Click-away layer: closes the menu without acting. */}
                          <div className="fixed inset-0 z-10" onClick={() => setDownloadFor(null)} />
                          <div className="absolute right-0 top-full mt-1 z-20 w-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1 text-left">
                            <button
                              onClick={() => { setDownloadFor(null); void exportQuotePdf(q); }}
                              className="w-full flex items-start gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <FileDown size={13} className="mt-0.5 text-accent shrink-0" />
                              <span>
                                <span className="block font-medium text-gray-800 dark:text-gray-100">PDF</span>
                                <span className="block text-gray-400">To send to the client</span>
                              </span>
                            </button>
                            <button
                              onClick={() => { setDownloadFor(null); void exportQuoteDocx(q); }}
                              className="w-full flex items-start gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                              <FileText size={13} className="mt-0.5 text-accent shrink-0" />
                              <span>
                                <span className="block font-medium text-gray-800 dark:text-gray-100">Word</span>
                                <span className="block text-gray-400">Editable copy</span>
                              </span>
                            </button>
                            <button
                              onClick={() => { setDownloadFor(null); void exportQuote(q); }}
                              className="w-full flex items-start gap-2 px-3 py-1.5 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 border-t border-gray-100 dark:border-gray-700"
                            >
                              <Download size={13} className="mt-0.5 text-gray-400 shrink-0" />
                              <span className="block text-gray-500 dark:text-gray-400">Data (JSON)</span>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                    {q.status === 'draft' && !q.supersededById && (
                      <button
                        onClick={() => {
                          if (confirm(`Delete ${q.quoteNumber}? Drafts only — issued quotes are records.`)) {
                            act(() => crmApi.deleteQuote(q._id), 'Quote deleted');
                          }
                        }}
                        title="Delete draft"
                        className="p-1 rounded text-gray-400 hover:text-red-500"
                      >
                        <Trash2 size={13} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {uploading && (
        <QuoteDocumentUpload quote={uploading.quote} kind={uploading.kind}
          onClose={() => setUploading(null)}
          onDone={() => { setUploading(null); void load(); }} />
      )}

      {editing && (
        <QuoteEditor
          quote={editing === 'new' ? null : editing}
          presetOpportunityId={editing === 'new' ? presetOpportunity : ''}
          opportunities={opportunities}
          products={products}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/**
 * Quote editor.
 *
 * Every total on screen comes from `POST /crm/quotes/preview`. Nothing is
 * computed here — the discount cap has to be applied by the same code that
 * enforces it, and pricing arithmetic in a browser is arithmetic nobody can
 * audit.
 */
function QuoteEditor({ quote, presetOpportunityId, opportunities, products, onClose, onSaved }: {
  quote: CrmQuote | null;
  presetOpportunityId?: string;
  opportunities: Opportunity[];
  products: CrmProduct[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const preset = opportunities.find((o) => o.id === presetOpportunityId);
  const [opportunityId, setOpportunityId] = useState(
    quote?.opportunityId || preset?.id || opportunities[0]?.id || '');
  const [title, setTitle] = useState(quote?.title || preset?.title || '');
  // A new quote starts in its deal's pricing region.
  const [regionCode, setRegionCode] = useState(quote?.regionCode || preset?.region || 'US');
  const sellers = useCrmSellerStore((s) => s.sellers);
  const deal = opportunities.find((o) => o.id === opportunityId);
  // End client, partner and seller. They belong to the DEAL — every quote on it
  // takes them from there — so a change here is saved onto the opportunity
  // first. Held as overrides of the deal's values until then.
  const [orgs, setOrgs] = useState<CrmClient[]>([]);
  const [picks, setPicks] = useState<{ clientId?: string; partnerOrgId?: string; sellerOrgId?: string }>({});
  useEffect(() => {
    crmApi.listClients().then((r) => setOrgs(r.clients || [])).catch(() => setOrgs([]));
  }, []);
  const partnerOptions = orgs.filter((o) => o.orgType === 'reseller' || o.orgType === 'parent');
  // An issued quote keeps the parties it was sent with; a draft follows its deal.
  const shown = quote && quote.status !== 'draft'
    ? { clientId: quote.clientId, partnerOrgId: quote.partnerOrgId, sellerOrgId: quote.sellerOrgId }
    : {
        clientId: picks.clientId ?? deal?.clientId ?? '',
        partnerOrgId: picks.partnerOrgId ?? deal?.partnerOrgId ?? '',
        sellerOrgId: picks.sellerOrgId ?? deal?.sellerOrgId ?? '',
      };
  const pickClient = (clientId: string) => {
    const chosen = orgs.find((o) => o._id === clientId);
    setPicks((p) => ({
      ...p, clientId,
      // The account's standing reseller, into an empty partner only.
      ...(chosen?.partnerOrgId && !(p.partnerOrgId ?? deal?.partnerOrgId)
        ? { partnerOrgId: chosen.partnerOrgId } : {}),
    }));
  };
  const [taxRatePct, setTaxRatePct] = useState(String(quote?.taxRatePct ?? 0));
  const [validUntil, setValidUntil] = useState(quote?.validUntil || '');
  const [lines, setLines] = useState<QuoteLineInput[]>(
    (quote?.lines || []).map((l) => ({
      sku: l.sku, quantity: l.quantity, discountPct: l.discountPct,
    })) || []);
  // Major milestones, unpriced — they never reach the preview, so they cannot
  // move a total. The server enforces the cap; the editor just stops offering.
  const [scope, setScope] = useState<QuoteScopeItem[]>(quote?.scopeItems || []);
  const [sowRef, setSowRef] = useState(quote?.sowRef || '');
  // The client's PO: part of the save on a draft; on an issued quote it is
  // recorded straight away, since it changes nothing the client was sent.
  const [poNumber, setPoNumber] = useState(quote?.poNumber || '');
  const savePoInPlace = async () => {
    if (!quote || poNumber === (quote.poNumber || '')) return;
    try {
      await crmApi.setQuotePo(quote._id, poNumber);
      toast.success('Purchase order number recorded');
    } catch (err: any) {
      toast.error(err.message || 'Could not record the PO number');
    }
  };
  const [rules, setRules] = useState<string[]>(
    (quote?.scheduleRules || []).map((r) => r.id));
  const [schedule, setSchedule] = useState<Record<string, any>>({});
  const [preview, setPreview] = useState<any>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    crmApi.getSettings()
      .then((s) => setSchedule(s.discountSchedule || {}))
      .catch(() => setSchedule({}));
  }, []);

  // Re-price on every change, server-side.
  useEffect(() => {
    let cancelled = false;
    crmApi.previewQuote({
      lines, regionCode, scheduleRules: rules,
      taxRatePct: Number(taxRatePct) || 0,
    })
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [lines, regionCode, rules, taxRatePct]);

  const setLine = (i: number, patch: Partial<QuoteLineInput>) =>
    setLines((ls) => ls.map((l, j) => (j === i ? { ...l, ...patch } : l)));

  const save = async () => {
    if (!opportunityId) {
      toast.error('A quote must belong to an opportunity');
      return;
    }
    setSaving(true);
    try {
      // Parties first, onto the deal, so the quote below inherits them.
      const dealChanges: Record<string, string> = {};
      if (picks.clientId !== undefined && picks.clientId !== (deal?.clientId || '')) {
        dealChanges.oppClientId = picks.clientId;
        const chosen = orgs.find((o) => o._id === picks.clientId);
        if (chosen && !deal?.account) dealChanges.oppAccount = chosen.name;
      }
      if (picks.partnerOrgId !== undefined && picks.partnerOrgId !== (deal?.partnerOrgId || '')) {
        dealChanges.oppPartnerOrgId = picks.partnerOrgId;
      }
      if (picks.sellerOrgId !== undefined && picks.sellerOrgId !== (deal?.sellerOrgId || '')) {
        dealChanges.oppSellerOrgId = picks.sellerOrgId;
      }
      if (Object.keys(dealChanges).length) {
        await crmApi.updateOpportunity(opportunityId, dealChanges);
      }
      const payload = {
        opportunityId, title, regionCode, validUntil,
        taxRatePct: Number(taxRatePct) || 0,
        scheduleRules: rules, lines,
        scopeItems: scope.filter((m) => m.text.trim()), sowRef,
        ...(issued ? {} : { poNumber }),
      };
      if (quote) {
        const res = await crmApi.updateQuote(quote._id, payload);
        toast.success(res.newVersion
          ? `New version created — v${res.quote.version}`
          : 'Quote updated');
      } else {
        const created = await crmApi.createQuote(payload);
        toast.success(`Created ${created.quoteNumber}`);
      }
      onSaved();
    } catch (err: any) {
      toast.error(err.message || 'Could not save the quote');
    } finally {
      setSaving(false);
    }
  };

  const field = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-accent';
  const label = 'block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1';
  const issued = quote && quote.status !== 'draft';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-xl bg-white dark:bg-gray-800 shadow-xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            {quote ? `${quote.quoteNumber} v${quote.version}` : 'New quote'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>

        {issued && (
          <div className="mx-5 mt-4 rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-xs text-blue-800 dark:text-blue-300">
            This quote has been issued. Saving creates <strong>version {(quote?.version || 1) + 1}</strong>;
            the figures already sent stay readable exactly as sent.
          </div>
        )}

        <div className="p-5 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={label}>Opportunity</label>
              <select className={field} value={opportunityId}
                disabled={!!quote}
                onChange={(e) => { setOpportunityId(e.target.value); setPicks({}); }}>
                {opportunities.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.displayId} — {o.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Title</label>
              <input className={field} value={title}
                onChange={(e) => setTitle(e.target.value)} />
            </div>
          </div>

          {/* Who buys, who it is sold through, who issues it. Saved onto the
              opportunity, which every quote on it follows. */}
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={label}>End client</label>
              <select className={field} value={shown.clientId || ''} disabled={!!issued}
                onChange={(e) => pickClient(e.target.value)}>
                <option value="">— not set —</option>
                {orgs.map((c) => (
                  <option key={c._id} value={c._id}>{c.name}{c.orgId ? ` · ${c.orgId}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Partner (reseller)</label>
              <select className={field} value={shown.partnerOrgId || ''} disabled={!!issued}
                onChange={(e) => setPicks((p) => ({ ...p, partnerOrgId: e.target.value }))}>
                <option value="">— not set —</option>
                {partnerOptions.map((r) => (
                  <option key={r._id} value={r.orgId || ''}>{r.name}{r.orgId ? ` · ${r.orgId}` : ''}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={label}>Issued by (selling as)</label>
              <select className={field} value={shown.sellerOrgId || ''} disabled={!!issued}
                onChange={(e) => setPicks((p) => ({ ...p, sellerOrgId: e.target.value }))}>
                <option value="">— not set —</option>
                {sellers.map((sl) => (
                  <option key={sl._id} value={sl.orgId}>{sl.name}</option>
                ))}
              </select>
            </div>
          </div>
          {Object.keys(picks).length > 0 && !issued && (
            <p className="-mt-2 text-[11px] text-gray-400 dark:text-gray-500">
              Saved onto {deal?.displayId || 'the opportunity'} too — every quote on a deal names the same parties.
            </p>
          )}

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className={label}>Region</label>
              <input className={field} value={regionCode}
                onChange={(e) => setRegionCode(e.target.value.toUpperCase())} />
            </div>
            <div>
              <label className={label}>Tax rate %</label>
              <input className={field} inputMode="decimal" value={taxRatePct}
                onChange={(e) => setTaxRatePct(e.target.value)} />
            </div>
            <div>
              <label className={label}>Valid until</label>
              <input type="date" className={field} value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)} />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label}>Line items</label>
              <button
                onClick={() => setLines((ls) => [...ls, { sku: '', quantity: 1, discountPct: 0 }])}
                className="text-xs text-accent"
              >
                + Add line
              </button>
            </div>
            <div className="space-y-2">
              {lines.map((l, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <select className={field} value={l.sku}
                    onChange={(e) => setLine(i, { sku: e.target.value })}>
                    <option value="">— choose a product —</option>
                    {products.map((p) => (
                      <option key={p._id} value={p.sku}>
                        {p.sku} · {p.name}
                      </option>
                    ))}
                  </select>
                  <input className={`${field} w-20`} inputMode="numeric"
                    title="Quantity"
                    value={l.quantity}
                    onChange={(e) => setLine(i, { quantity: Number(e.target.value) || 1 })} />
                  <input className={`${field} w-24`} inputMode="decimal"
                    title="Discount %"
                    placeholder="disc %"
                    value={l.discountPct ?? 0}
                    onChange={(e) => setLine(i, { discountPct: Number(e.target.value) || 0 })} />
                  <button onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}
                    className="px-2 py-2 text-gray-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {lines.length === 0 && (
                <p className="text-xs text-gray-400">No lines yet.</p>
              )}
            </div>
          </div>

          {/* Scope: the major milestones as bullets, with no price. Detail
              belongs in the Statement of Work referenced below. */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={label}>
                Scope — major milestones{' '}
                <span className="font-normal text-gray-400">
                  ({scope.length}/{MAX_SCOPE_ITEMS}, not priced)
                </span>
              </label>
              {scope.length < MAX_SCOPE_ITEMS && (
                <button
                  onClick={() => setScope((ms) => [...ms, { text: '', target: '' }])}
                  className="text-xs text-accent"
                >
                  + Add milestone
                </button>
              )}
            </div>
            <div className="space-y-2">
              {scope.map((m, i) => (
                <div key={i} className="flex gap-2 items-start">
                  <span className="pt-2 text-gray-400">•</span>
                  <input className={field} maxLength={200}
                    placeholder="e.g. Platform provisioned and SSO connected"
                    value={m.text}
                    onChange={(e) => setScope((ms) => ms.map((x, j) =>
                      (j === i ? { ...x, text: e.target.value } : x)))} />
                  <input className={`${field} w-36`} maxLength={60}
                    placeholder="When — Week 4"
                    title="When it lands: a date or a week number"
                    value={m.target || ''}
                    onChange={(e) => setScope((ms) => ms.map((x, j) =>
                      (j === i ? { ...x, target: e.target.value } : x)))} />
                  <button onClick={() => setScope((ms) => ms.filter((_, j) => j !== i))}
                    className="px-2 py-2 text-gray-400 hover:text-red-500">
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {scope.length === 0 && (
                <p className="text-xs text-gray-400">No milestones yet.</p>
              )}
            </div>
          </div>

          <div>
            <label className={label}>Client purchase order number</label>
            <input className={field} maxLength={60} value={poNumber}
              placeholder="Leave blank for the client to fill in"
              onChange={(e) => setPoNumber(e.target.value)}
              onBlur={() => { if (issued) void savePoInPlace(); }} />
            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              Printed on the acceptance block's order-number line.
              {issued && ' Recorded on this version as soon as you leave the field — it does not create a new version.'}
            </p>
          </div>

          <div>
            <label className={label}>Statement of Work</label>
            <input className={field} maxLength={200} value={sowRef}
              placeholder="SOW reference or link"
              onChange={(e) => setSowRef(e.target.value)} />
            <p className="mt-1 text-[11px] text-gray-400 dark:text-gray-500">
              The SOW this quote is delivered under. Shown on the PDF; the full
              breakdown lives there.
            </p>
          </div>

          {Object.keys(schedule).length > 0 && (
            <div>
              <label className={label}>Discount schedule</label>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(schedule).map(([key, rule]: [string, any]) => (
                  <label key={key}
                    className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border cursor-pointer text-xs ${
                      rules.includes(key)
                        ? 'border-accent/40 bg-accent/5 text-accent'
                        : 'border-gray-200 dark:border-gray-600 text-gray-500'}`}>
                    <input type="checkbox" className="sr-only"
                      checked={rules.includes(key)}
                      onChange={() => setRules((rs) =>
                        rs.includes(key) ? rs.filter((r) => r !== key) : [...rs, key])} />
                    {key.replace(/_/g, ' ')} {rule.discount_pct}%
                  </label>
                ))}
              </div>
            </div>
          )}

          {preview && (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-1 text-sm">
              {[
                ['Gross', money(preview.grossMinor)],
                ['Line discounts', `- ${money(preview.lineDiscountMinor)}`],
                ['Schedule discount', `- ${money(preview.scheduleDiscountMinor)}`],
                ['Net', money(preview.netMinor)],
                [`Tax (${preview.taxRatePct || 0}%)`, money(preview.taxMinor)],
              ].map(([k, v], i) => (
                <div key={i} className="flex justify-between text-gray-600 dark:text-gray-300">
                  <span>{k}</span><span className="tabular-nums">{v}</span>
                </div>
              ))}
              <div className="flex justify-between pt-1 border-t border-gray-100 dark:border-gray-700 font-semibold text-gray-900 dark:text-gray-100">
                <span>Total</span>
                <span className="tabular-nums">{money(preview.totalMinor)}</span>
              </div>
              {preview.localCurrency !== 'USD' && (
                <div className="flex justify-between text-xs text-gray-400">
                  <span>In {preview.localCurrency} @ {preview.exchangeRate}</span>
                  <span className="tabular-nums">
                    {money(preview.totalLocalMinor, preview.localCurrency)}
                  </span>
                </div>
              )}

              {preview.discountCapped && (
                <div className="flex gap-2 mt-2 text-xs text-amber-700 dark:text-amber-300">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>
                    The selected rules total {preview.scheduleDiscountPct}%, above the{' '}
                    {preview.discountCapPct}% ceiling. Held at {preview.discountCapPct}%.
                  </span>
                </div>
              )}
              {preview.requiresApproval && (
                <div className="flex gap-2 mt-2 text-xs text-amber-700 dark:text-amber-300">
                  <ShieldCheck size={13} className="shrink-0 mt-0.5" />
                  <span>Needs approval before it can be issued.</span>
                </div>
              )}
              {(preview.violations || []).map((v: string, i: number) => (
                <div key={i} className="flex gap-2 mt-2 text-xs text-rose-600 dark:text-rose-400">
                  <AlertTriangle size={13} className="shrink-0 mt-0.5" />
                  <span>{v}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100 dark:border-gray-700">
          <button onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300">
            Cancel
          </button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1.5 text-sm rounded-lg bg-accent text-white disabled:opacity-50">
            {saving ? 'Saving…' : issued ? 'Save as new version' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
